import { call, put, takeEvery, all, select } from 'redux-saga/effects';
import { Routine } from 'redux-saga-routines';
import LogRocket from 'logrocket';
import {
  fetchRefRequestRoutine,
  saveReferencesRoutine,
  confirmPrivacyPolicyRoutine,
  addPhoneNumberRoutine,
  checkCodeRoutine,
  resendSurveyRoutine,
  checkKeyRoutine,
  checkPhoneNumberRoutine
} from '../routines';
import { setCandidateConsent, checkCode, checkKey } from '../../../services/candidateService';
import { addPhoneNumber, checkRefPhoneNumber } from '../../../services/contactService';
import { saveReferences, resendSurvey } from '../../../services/referenceService';
import { getByKey } from '../../../services/referenceRequestService';
import { setCode } from '../../../common/helpers/common';
import { setWorkspaceDataRoutine } from '../../../routines/workspace';
import { IAppState } from '../../../common/models/store/IAppState';
import { IRefReqCandidateForm } from '../../../common/models/referenceRequest/IRefReqCandidateForm';

function* identifyCandidate(response: IRefReqCandidateForm) {
  const workspaceName = yield select(({ workspace }: IAppState) => workspace.name);
  const name = `${response.firstName} ${response.lastName}`;
  const { contactId: id } = response;

  LogRocket.identify(id, {
    name,
    email: `${workspaceName} - Candidate`,
    contactEmail: response.email
  });
  heap.identify(id);
  heap.addUserProperties({ name, workspace: workspaceName });
}

function* checkKeyRequest() {
  try {
    const response = yield call(checkKey);
    yield put(setWorkspaceDataRoutine.trigger(response));
    yield put(checkKeyRoutine.success());
  } catch (error) {
    yield put(checkKeyRoutine.failure(error.message));
  }
}

function* checkCodeRequest({ payload }: Routine<any>) {
  try {
    const response = yield call(checkCode, payload);
    setCode(payload);
    yield put(checkCodeRoutine.success(response));
    yield identifyCandidate(response);
  } catch (error) {
    yield put(checkCodeRoutine.failure(error.message));
  }
}

function* getByCandidateIdRequest() {
  try {
    const { workspace, ...response } = yield call(getByKey);
    yield put(setWorkspaceDataRoutine.trigger(workspace));
    yield put(fetchRefRequestRoutine.success(response));
    yield identifyCandidate(response);
  } catch {
    yield put(fetchRefRequestRoutine.failure());
  }
}

function* saveRequestsRequest({ payload }: Routine<any>) {
  try {
    const response = yield call(saveReferences, payload);
    const { candidateId } = payload;

    yield put(saveReferencesRoutine.success(response));
    yield put(fetchRefRequestRoutine.trigger(candidateId));
  } catch {
    yield put(saveReferencesRoutine.failure());
  }
}

function* setCandidateConsentRequest() {
  try {
    yield call(setCandidateConsent);

    yield put(confirmPrivacyPolicyRoutine.success());
  } catch {
    yield put(confirmPrivacyPolicyRoutine.failure());
  }
}

function* addPhoneNumberRequest({ payload }: Routine<any>) {
  try {
    yield call(addPhoneNumber, payload);
    yield put(addPhoneNumberRoutine.success(payload));
  } catch {
    yield put(addPhoneNumberRoutine.failure());
  }
}

function* resendSurveyRequest({ payload }: Routine<any>) {
  try {
    yield call(resendSurvey, payload);
    yield put(resendSurveyRoutine.success());
  } catch {
    yield put(resendSurveyRoutine.failure());
  }
}

function* checkPhoneNumberRequest({ payload }: Routine<any>) {
  try {
    const { onSuccess, phoneNumber } = payload;
    const { isValid } = yield call(checkRefPhoneNumber, phoneNumber);
    if (isValid) {
      yield put(checkPhoneNumberRoutine.success());
      onSuccess();
    } else {
      yield put(checkPhoneNumberRoutine.failure(phoneNumber));
    }
  } catch {
    yield put(checkPhoneNumberRoutine.failure());
  }
}

function* watchCheckKeyRequest() {
  yield takeEvery(checkKeyRoutine.TRIGGER, checkKeyRequest);
}

function* watchCheckCodeRequest() {
  yield takeEvery(checkCodeRoutine.TRIGGER, checkCodeRequest);
}

function* watchGetByCandidateIdRequest() {
  yield takeEvery(fetchRefRequestRoutine.TRIGGER, getByCandidateIdRequest);
}

function* watchSaveRequestsRequest() {
  yield takeEvery(saveReferencesRoutine.TRIGGER, saveRequestsRequest);
}

function* watchSetCandidateConsentRequest() {
  yield takeEvery(confirmPrivacyPolicyRoutine.TRIGGER, setCandidateConsentRequest);
}

function* watchAddPhoneNumberRequest() {
  yield takeEvery(addPhoneNumberRoutine.TRIGGER, addPhoneNumberRequest);
}

function* watchResendSurveyRequest() {
  yield takeEvery(resendSurveyRoutine.TRIGGER, resendSurveyRequest);
}

function* watchCheckPhoneNumberRequest() {
  yield takeEvery(checkPhoneNumberRoutine.TRIGGER, checkPhoneNumberRequest);
}

export default function* candidateFormSaga() {
  yield all([
    watchGetByCandidateIdRequest(),
    watchSaveRequestsRequest(),
    watchSetCandidateConsentRequest(),
    watchAddPhoneNumberRequest(),
    watchCheckCodeRequest(),
    watchResendSurveyRequest(),
    watchCheckKeyRequest(),
    watchCheckPhoneNumberRequest()
  ]);
}
